import type { QueryClient } from '@tanstack/react-query';
import { queries } from '../../api/queries.js';
import type { CatalogEvent, StreamOverview, StreamsResponse } from '../../api/types.js';
import { loadBroker } from '../brokers/selection.js';

/** Both catalogue routes read the same two answers; one loader keeps them from drifting apart. */
export async function loadCatalog(queryClient: QueryClient): Promise<unknown> {
  // What the broker holds is per broker; what the packages declare is not. Only the first
  // waits on the selection.
  const events = queryClient.ensureQueryData(queries.events());
  const broker = await loadBroker(queryClient);
  return Promise.all([events, queryClient.ensureQueryData(queries.streams(broker))]);
}

/** The newest major of its base subject — the contract new payloads belong on. */
export function isCurrent(event: Pick<CatalogEvent, 'supersededBy'>): boolean {
  return !event.supersededBy;
}

/** A stream with the contract events that write into it, as the catalogue shows them. */
export interface StreamSummary {
  /** From the broker where it holds one, from the packages where it does not. */
  name: string;
  /**
   * What the broker holds, absent when it holds no stream of this name.
   *
   * The packages declare the stream each event belongs to, and that declaration is a fact
   * about the contract, not about any one broker. A stream nobody has provisioned yet is
   * therefore a normal state — not an error, and not a reason to drop its events from the
   * catalogue, which is the whole reason this is optional rather than required.
   */
  stream?: StreamOverview;
  /** The subject patterns captured — the broker's where it exists, the declared ones otherwise. */
  captures: string[];
  events: CatalogEvent[];
  currentCount: number;
  /** `@mg/events-hubspot` and friends — the packages whose events land here. */
  packages: string[];
  /** Any consumer with a backlog or a redelivery. */
  behind: boolean;
}

/** `@mg/events-hubspot`, one per producer writing here. */
function packagesOf(events: CatalogEvent[]): string[] {
  const producers = [...new Set(events.map((event) => event.producer))].sort();
  return producers.map((producer) => `@mg/events-${producer}`);
}

export function summarizeStream(stream: StreamOverview, events: CatalogEvent[]): StreamSummary {
  const own = events.filter((event) => event.stream === stream.name);
  return {
    name: stream.name,
    stream,
    captures: stream.subjects,
    events: own,
    currentCount: own.filter(isCurrent).length,
    packages: packagesOf(own),
    behind: stream.consumers.some((consumer) => consumer.pending > 0 || consumer.redelivered > 0),
  };
}

/**
 * A stream the packages declare that this broker does not hold.
 *
 * `captures` is derived as `<producer>.>` rather than listed subject by subject, because that
 * is the wildcard the registry's own rendered resource uses. It describes what the stream
 * would capture once somebody provisions it, which is the question the row raises.
 */
export function summarizeDeclaredStream(name: string, events: CatalogEvent[]): StreamSummary {
  const own = events.filter((event) => event.stream === name);
  return {
    name,
    captures: [...new Set(own.map((event) => `${event.producer}.>`))].sort(),
    events: own,
    currentCount: own.filter(isCurrent).length,
    packages: packagesOf(own),
    behind: false,
  };
}

/**
 * Every stream in the catalogue: the ones the broker holds, then the ones only the packages
 * know about.
 *
 * Listing the second kind is the difference between a catalogue of a broker and a catalogue of
 * the contracts. Keyed on the broker alone, an event whose stream was never provisioned simply
 * vanished — on staging that hid every `hubspot.*` and `portal.*` event, which are exactly the
 * ones somebody goes looking for when they cannot find where an event is supposed to land.
 * Held streams stay first, so "what is live here" is still the top of the table.
 */
export function summarizeStreams(streams: StreamOverview[], events: CatalogEvent[]): StreamSummary[] {
  const held = streams.map((stream) => summarizeStream(stream, events));
  return [...held, ...declaredOnly(streams, events).map((name) => summarizeDeclaredStream(name, events))];
}

/** The stream names the packages declare that no stream on this broker carries. */
export function declaredOnly(streams: StreamOverview[], events: CatalogEvent[]): string[] {
  const onBroker = new Set(streams.map((stream) => stream.name));
  return [...new Set(events.map((event) => event.stream))].filter((name) => !onBroker.has(name)).sort();
}

/** Message counts per subject, flattened across streams — a subject lives in exactly one. */
export function subjectCounts(streams: StreamsResponse): Map<string, number> {
  const counts = new Map<string, number>();
  for (const stream of streams.streams) {
    for (const [subject, count] of Object.entries(stream.perSubject)) counts.set(subject, count);
  }
  return counts;
}
